import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Collapse, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

// Sections ordered to match Tableau nav: Medical > Pharmacy > Risk > Health Plan > Care Management > Opportunity Discovery > MedPrice Insight
const NAV_SECTIONS = [
  {
    id: 'medical',
    label: 'Medical',
    items: [
      { label: 'Large Claims',          path: '/dashboard',    phase: 1 },
      { label: 'Claims Watchlist',      path: '/watchlist',    phase: 1 },
      { label: 'Total Cost',            path: '/total-cost',   phase: 1 },
      { label: 'ER Cost & Utilization', path: '/er-cost',      phase: 1 },
      { label: 'Claims Profile',        path: '/claims-profile', phase: 3 },
      { label: 'Special Cases',         path: '/special-cases', phase: 3 },
      { label: 'Premiums & Adjustments',path: '/premiums-adj', phase: 3 },
    ],
  },
  {
    id: 'pharmacy',
    label: 'Pharmacy',
    items: [
      { label: 'Rx Management',           path: '/rx-management',      phase: 2 },
      { label: 'High Caution Meds',       path: '/high-caution-rx',    phase: 2 },
      { label: 'Branded → Generic',       path: '/branded-generic',    phase: 3 },
      { label: 'Best Buy Generic',        path: '/best-buy-generic',   phase: 3 },
      { label: 'Generic Equivalent',      path: '/generic-equivalent', phase: 3 },
      { label: 'Therapeutic Alternative', path: '/therapeutic-alt',    phase: 3 },
    ],
  },
  {
    id: 'risk',
    label: 'Risk',
    items: [
      { label: 'Utilization Cost',       path: '/utilization',        phase: 1 },
      { label: 'High Cost Members',      path: '/high-cost-members',  phase: 1 },
      { label: 'High Cost Groups',       path: '/high-cost-groups',   phase: 2 },
      { label: 'Disease Prevalence',     path: '/disease-prevalence', phase: 2 },
      { label: 'Risk Matrix',            path: '/risk-matrix',        phase: 2 },
      { label: 'Age & Gender Profile',   path: '/age-gender',         phase: 2 },
      { label: 'Probability Profile',    path: '/probability',        phase: 2 },
      { label: 'Provider Risk Profile',  path: '/provider-risk',      phase: 2 },
    ],
  },
  {
    id: 'health-plan',
    label: 'Health Plan',
    items: [
      { label: 'Premiums',               path: '/premiums',       phase: 3 },
      { label: 'Stop Loss',              path: '/stop-loss',      phase: 3 },
      { label: 'Plan Manager',           path: '/plan-manager',   phase: 3 },
      { label: 'Automated Renewals 1',   path: '/renewals-1',     phase: 3 },
      { label: 'Automated Renewals 2',   path: '/renewals-2',     phase: 3 },
    ],
  },
  {
    id: 'care-mgmt',
    label: 'Care Management',
    items: [
      { label: 'Case Management',        path: '/case-management', phase: 2 },
      { label: 'Utilization Management', path: '/util-management', phase: 2 },
      { label: 'SDOH',                   path: '/sdoh',            phase: 3 },
    ],
  },
  {
    id: 'opportunity',
    label: 'Opportunity Discovery',
    items: [
      { label: 'Payment Accuracy',     path: '/payment-accuracy',   phase: 3 },
      { label: 'Administered Drugs',   path: '/administered-drugs', phase: 3 },
      { label: 'DME',                  path: '/dme',                phase: 3 },
      { label: 'Clinical Lab',         path: '/clinical-lab',       phase: 3 },
    ],
  },
  {
    id: 'medprice',
    label: 'MedPrice Insight',
    items: [
      { label: 'Pricing Transparency', path: '/pricing-transparency', phase: 3 },
    ],
  },
];


function NavSection({ section, collapsed, onToggle }) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <Box>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 0.75, cursor: 'pointer',
          '&:hover': { background: 'rgba(102,126,234,0.06)' },
        }}
      >
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em',
          textTransform: 'uppercase', color: 'var(--text-secondary)', }}>
          {section.label}
        </Typography>
        {collapsed
          ? <ExpandMore sx={{ fontSize: 16, color: 'var(--text-secondary)' }} />
          : <ExpandLess sx={{ fontSize: 16, color: 'var(--text-secondary)' }} />}
      </Box>

      <Collapse in={!collapsed}>
        {section.items.filter(item => item.phase <= 3).map(item => {
          const active = location.pathname === item.path;
          return (
            <Box
              key={item.path}
              onClick={() => navigate(item.path)}
              sx={{
                display: 'flex', alignItems: 'center',
                px: 2.5, py: 0.6,
                cursor: 'pointer',
                background: active ? 'rgba(102,126,234,0.12)' : 'transparent',
                borderLeft: active ? '2px solid #667eea' : '2px solid transparent',
                '&:hover': { background: 'rgba(102,126,234,0.07)' },
              }}
            >
              <Typography sx={{
                fontSize: '0.82rem',
                fontWeight: active ? 600 : 400,
                color: active ? '#667eea' : 'var(--text-primary)',
              }}>
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Collapse>
    </Box>
  );
}

export default function DashboardNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const activeSection = NAV_SECTIONS.find(s => s.items.some(i => location.pathname === i.path));
  const [collapsed, setCollapsed] = useState(
    Object.fromEntries(NAV_SECTIONS.map(s => [s.id, s.id !== activeSection?.id]))
  );

  const toggle = id => setCollapsed(p => ({ ...p, [id]: !p[id] }));

  return (
    <Box sx={{
      width: 200,
      height: '100vh',
      position: 'fixed',
      left: 0,
      top: 0,
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--bg-primary, #fff)',
      borderRight: '1px solid var(--border-color, #e5e7eb)',
      overflowY: 'auto',
      zIndex: 100,
      '&::-webkit-scrollbar': { width: 4 },
      '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 2 },
    }}>
      {/* Logo */}
      <Box
        onClick={() => navigate('/dashboard')}
        sx={{
          px: 2.5, py: 2, cursor: 'pointer', flexShrink: 0,
          borderBottom: '1px solid var(--border-color, #e5e7eb)',
          display: 'flex', alignItems: 'center', gap: 1,
        }}
      >
        <Box sx={{
          width: 28, height: 28, borderRadius: 1.5,
          background: 'linear-gradient(135deg, #667eea, #8b5cf6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 800 }}>V</Typography>
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)' }}>
          Veris
        </Typography>
      </Box>

      {/* Member Profile link — above all sections */}
      <Box sx={{ borderBottom: '1px solid var(--border-color, #e5e7eb)', py: 0.5 }}>
        <Box
          onClick={() => navigate('/member')}
          sx={{
            display: 'flex', alignItems: 'center', px: 2.5, py: 0.75, cursor: 'pointer',
            background: location.pathname.startsWith('/member') ? 'rgba(102,126,234,0.12)' : 'transparent',
            borderLeft: location.pathname.startsWith('/member') ? '2px solid #667eea' : '2px solid transparent',
            '&:hover': { background: 'rgba(102,126,234,0.07)' },
          }}
        >
          <Typography sx={{ fontSize: '0.82rem', fontWeight: location.pathname.startsWith('/member') ? 600 : 400, color: location.pathname.startsWith('/member') ? '#667eea' : 'var(--text-primary)' }}>
            Member Profile
          </Typography>
        </Box>
      </Box>

      {/* Nav sections */}
      <Box sx={{ py: 1, flex: 1 }}>
        {NAV_SECTIONS.filter(s => s.items.some(i => i.phase <= 3)).map(section => (
          <NavSection
            key={section.id}
            section={section}
            collapsed={collapsed[section.id]}
            onToggle={() => toggle(section.id)}
          />
        ))}
      </Box>

    </Box>
  );
}
